/**
 * Parameter experiments (CLAUDE.md §4): specs live in config/experiments/*.json and are bundled at
 * build time. Assignment is deterministic per anonymous install id, never per player value or
 * spend (H3/H6); `?exp-<key>=<variant>` overrides it for QA and the video factory.
 */
import { hashSeed } from "@enjoyment/core";
import hdPictures from "../../../../../config/experiments/mozaik-hd-pictures.json";

export interface ExperimentSpec {
  key: string;
  status: string;
  default: string;
  variants: { key: string; weight: number }[];
}

const ANON_KEY = "enjoyment.anon";

/** Random, local-only install id used solely for bucketing (H10: no profile, never sent alone). */
function anonId(): string {
  try {
    const saved = localStorage.getItem(ANON_KEY);
    if (saved) return saved;
    const id = crypto.randomUUID();
    localStorage.setItem(ANON_KEY, id);
    return id;
  } catch {
    return "no-storage";
  }
}

/** Pure bucketing: the same key + id always lands in the same variant. */
export function assign(spec: ExperimentSpec, id: string, override?: string | null): string {
  if (override && spec.variants.some((v) => v.key === override)) return override;
  if (spec.status !== "running") return spec.default;
  const total = spec.variants.reduce((n, v) => n + v.weight, 0);
  if (total <= 0) return spec.default;
  let roll = hashSeed(`${spec.key}:${id}`) % total;
  for (const v of spec.variants) {
    if (roll < v.weight) return v.key;
    roll -= v.weight;
  }
  return spec.default;
}

function variantOf(spec: ExperimentSpec): string {
  let override: string | null = null;
  try {
    override = new URLSearchParams(location.search).get(`exp-${spec.key}`);
  } catch {
    /* no location (tests) */
  }
  return assign(spec, anonId(), override);
}

export const HD_PICTURES_SPEC = hdPictures as ExperimentSpec;
let hdVariant: string | undefined;

/** Variant of the HD picture experiment for this install ("hd" | "pixel"). */
export function hdPicturesVariant(): string {
  hdVariant ??= variantOf(HD_PICTURES_SPEC);
  return hdVariant;
}

/** Event properties every telemetry event carries while experiments run. */
export function experimentProps(): Record<string, string> {
  return { exp_mozaik_hd_pictures: hdPicturesVariant() };
}
